import React from 'react';
import { Drawer, Card, Typography, Space } from 'antd';
import { 
  BrainCircuit, 
  Wrench, 
  GitBranch, 
  Play, 
  Square,
  MessageSquare,
  Plus,
  Database,
  Globe,
  Mail,
  Settings,
  Zap,
  FileText
} from 'lucide-react';

const { Title, Text } = Typography;

interface NodePaletteProps {
  visible: boolean;
  onClose: () => void;
}

const NodePalette: React.FC<NodePaletteProps> = ({ visible, onClose }) => {
  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
    
    // Change cursor during drag
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.style.cursor = 'grabbing';
    }
  };

  const nodeTypes = [
    {
      type: 'start',
      icon: <Play className="h-5 w-5 text-green-600" />,
      title: 'Start Node',
      description: 'Entry point for the workflow',
      color: 'border-green-200 bg-green-50'
    },
    {
      type: 'input',
      icon: <MessageSquare className="h-5 w-5 text-blue-600" />,
      title: 'Input Node',
      description: 'Capture user input for the workflow',
      color: 'border-blue-200 bg-blue-50'
    },
    {
      type: 'end',
      icon: <Square className="h-5 w-5 text-red-600" />,
      title: 'End Node',
      description: 'Exit point for the workflow',
      color: 'border-red-200 bg-red-50'
    },
    {
      type: 'llm',
      icon: <BrainCircuit className="h-5 w-5 text-purple-600" />,
      title: 'LLM Node',
      description: 'Large Language Model processing',
      color: 'border-purple-200 bg-purple-50'
    },
    {
      type: 'tool',
      icon: <Wrench className="h-5 w-5 text-orange-600" />,
      title: 'Tool Node',
      description: 'Execute specific tools and functions',
      color: 'border-orange-200 bg-orange-50'
    },
    {
      type: 'condition',
      icon: <GitBranch className="h-5 w-5 text-cyan-600" />,
      title: 'Condition Node',
      description: 'Conditional branching logic',
      color: 'border-cyan-200 bg-cyan-50'
    }
  ];

  const toolPresets = [
    {
      name: 'Web Search',
      tool: 'webSearch',
      description: 'Search the web for information'
    },
    {
      name: 'Vector Search',
      tool: 'vectorSearch',
      description: 'Search documents using vector similarity'
    },
    {
      name: 'Hybrid Search',
      tool: 'hybridSearch',
      description: 'Combine keyword and semantic search'
    },
    {
      name: 'Response Evaluation',
      tool: 'responseEvaluation',
      description: 'Evaluate the quality of a generated response'
    },
    {
      name: 'Context Selection',
      tool: 'contextSelection',
      description: 'Select the best context from search results'
    }
  ];

  const llmPresets = [
    {
      name: 'Response Generator',
      model: 'gpt-4-turbo-preview',
      description: 'Generate comprehensive responses'
    },
    {
      name: 'Query Analyzer',
      model: 'gpt-3.5-turbo',
      description: 'Analyze user intent and extract entities'
    },
    {
      name: 'Content Summarizer',
      model: 'gpt-4-turbo-preview',
      description: 'Summarize large content blocks'
    },
    {
      name: 'JSON Formatter',
      model: 'gpt-4-turbo-preview',
      description: 'Format JSON data into readable paragraphs with highlights',
      customCode: `// JSON Formatter LLM Node
// Detects JSON content and formats it beautifully
async function processLLM(state, setOutput, tools) {
  try {
    const input = state.input || state.output || '';
    
    // Check if input contains JSON-like content
    const hasJsonPattern = /\\{.*\\}|\\[.*\\]|"\\w+"\\s*:|Search results for|weather|location|current|temperature/i.test(input);
    
    if (!hasJsonPattern) {
      // If no JSON detected, pass through unchanged
      setOutput(input);
      return { success: true };
    }
    
    const prompt = \`You are a content formatter specializing in making technical data readable and visually appealing.

**Task**: Convert the following raw data into a well-formatted, easy-to-read response with:
1. Clear paragraph structure
2. **Bold highlighting** for important information (names, numbers, key values)
3. Bullet points for lists
4. Natural, conversational flow
5. Remove technical JSON structure while preserving all information

**Input Data**:
\${input}

**Format Guidelines**:
- Start with a clear summary sentence
- Use **bold** for: location names, temperatures, dates, times, important numbers
- Use bullet points for multiple items or details
- Write in natural paragraphs, not technical format
- Keep all important information but make it human-readable
- If it's weather data, format like a weather report
- If it's search results, format like organized findings

**Output only the formatted content, nothing else**:\`;

    const response = await tools.llm.invoke({
      model: config.model || 'gpt-4-turbo-preview',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.3,
      maxTokens: 1500
    });
    
    setOutput(response.content);
    return { success: true };
  } catch (error) {
    console.error('JSON Formatter failed:', error);
    setOutput('Error formatting content: ' + error.message);
    return { success: false, error: error.message };
  }
}`
    }
  ];

  const systemNodes = [
    {
      type: 'sql-query',
      icon: <Database className="h-5 w-5 text-green-600" />,
      title: 'SQL Query',
      description: 'Execute SQL queries on connected databases',
      color: 'border-green-200 bg-green-50',
      defaultConfig: {
        label: 'SQL Query',
        dataSourceId: '',
        query: 'SELECT * FROM table_name WHERE condition = {{variable}}',
        outputFormat: 'json',
        resultLimit: 100,
        timeout: 30000
      }
    },
    {
      type: 'http-request',
      icon: <Globe className="h-5 w-5 text-blue-600" />,
      title: 'HTTP Request',
      description: 'Make HTTP API calls to external services',
      color: 'border-blue-200 bg-blue-50',
      defaultConfig: {
        label: 'HTTP Request',
        url: 'https://api.example.com/data',
        method: 'GET',
        headers: {},
        responseFormat: 'json',
        timeout: 30000
      }
    },
    {
      type: 'data-transform',
      icon: <Zap className="h-5 w-5 text-purple-600" />,
      title: 'Transform Data',
      description: 'Transform data using JavaScript code',
      color: 'border-purple-200 bg-purple-50',
      defaultConfig: {
        label: 'Transform Data',
        code: 'return data.map(item => ({ ...item, processed: true }));',
        timeout: 10000
      }
    },
    {
      type: 'email',
      icon: <Mail className="h-5 w-5 text-red-600" />,
      title: 'Send Email',
      description: 'Send email notifications and reports',
      color: 'border-red-200 bg-red-50',
      defaultConfig: {
        label: 'Send Email',
        to: '{{userEmail}}',
        subject: 'Workflow Notification',
        template: 'default',
        attachData: false
      }
    },
    {
      type: 'file-operation',
      icon: <FileText className="h-5 w-5 text-orange-600" />,
      title: 'File Operation',
      description: 'Read, write, or process files',
      color: 'border-orange-200 bg-orange-50',
      defaultConfig: {
        label: 'File Operation',
        operation: 'read',
        filePath: '/path/to/file.txt',
        encoding: 'utf8'
      }
    },
    {
      type: 'cache',
      icon: <Settings className="h-5 w-5 text-gray-600" />,
      title: 'Cache Operation',
      description: 'Store and retrieve data from cache',
      color: 'border-gray-200 bg-gray-50',
      defaultConfig: {
        label: 'Cache Operation',
        operation: 'get',
        key: 'cache_key_{{timestamp}}',
        ttl: 3600
      }
    }
  ];


  return (
    <Drawer
      title="Node Palette"
      placement="left"
      width={350}
      onClose={onClose}
      open={visible}
      styles={{ 
        body: { padding: '16px', position: 'relative', zIndex: 10000, width: '350px' },
        mask: { display: 'none !important', width: '0 !important' },
        wrapper: { 
          zIndex: 10000, 
          position: 'fixed', 
          width: '350px !important',
          maxWidth: '350px !important',
          left: '0 !important',
          right: 'auto !important'
        },
        content: { 
          zIndex: 10000, 
          width: '350px !important',
          maxWidth: '350px !important'
        },
        header: { 
          zIndex: 10000, 
          width: '350px !important',
          maxWidth: '350px !important'
        }
      }}
      mask={false}
      getContainer={false}
      zIndex={10000}
      rootStyle={{ 
        position: 'relative', 
        zIndex: 10000,
        width: '350px !important',
        maxWidth: '350px !important',
        left: '0 !important',
        right: 'auto !important'
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Basic Node Types */}
        <div>
          <Title level={5}>Basic Nodes</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            {nodeTypes.map((node) => (
              <Card
                key={node.type}
                size="small"
                style={{ 
                  cursor: 'grab',
                  border: '2px dashed #d9d9d9',
                  marginBottom: '8px',
                  transition: 'all 0.3s'
                }}
                hoverable
                draggable
                onDragStart={(event) => onDragStart(event, node.type)}
                onDragEnd={(event) => {
                  event.currentTarget.style.cursor = 'grab';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flexShrink: 0 }}>
                    {node.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '14px' }}>{node.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{node.description}</div>
                  </div>
                  <Plus style={{ width: '16px', height: '16px', color: '#999' }} />
                </div>
              </Card>
            ))}
          </Space>
        </div>

        {/* System Nodes */}
        <div>
          <Title level={5}>System Nodes</Title>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Execute system operations like SQL queries, API calls, and data transformations
          </Text>
          <div style={{ marginTop: '8px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {systemNodes.map((node) => (
                <Card
                  key={node.type}
                  size="small"
                  style={{
                    cursor: 'grab',
                    border: '2px dashed #52c41a',
                    backgroundColor: '#f6ffed',
                    marginBottom: '8px',
                    transition: 'all 0.3s'
                  }}
                  hoverable
                  draggable
                  onDragStart={(event) => {
                    const nodeData = {
                      type: node.type,
                      data: node.defaultConfig
                    };
                    
                    const dataString = JSON.stringify(nodeData);
                    event.dataTransfer.setData('application/reactflow', dataString);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={(event) => {
                    event.currentTarget.style.cursor = 'grab';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flexShrink: 0 }}>
                      {node.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>{node.title}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{node.description}</div>
                    </div>
                    <Plus style={{ width: '12px', height: '12px', color: '#999' }} />
                  </div>
                </Card>
              ))}
            </Space>
          </div>
        </div>

        {/* Tool Presets */}
        <div>
          <Title level={5}>Tool Presets</Title>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Pre-configured tool nodes for common RAG operations
          </Text>
          <div style={{ marginTop: '8px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {toolPresets.map((preset) => (
                <Card
                  key={preset.tool}
                  size="small"
                  style={{
                    cursor: 'grab',
                    border: '2px dashed #ffa940',
                    backgroundColor: '#fff7e6',
                    marginBottom: '8px',
                    transition: 'all 0.3s'
                  }}
                  hoverable
                  draggable
                  onDragStart={(event) => {
                    const nodeData = {
                      type: 'tool',
                      data: {
                        label: preset.name,
                        tool: preset.tool,
                        description: preset.description
                      }
                    };
                    
                    const dataString = JSON.stringify(nodeData);
                    event.dataTransfer.setData('application/reactflow', dataString);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={(event) => {
                    event.currentTarget.style.cursor = 'grab';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Wrench style={{ width: '16px', height: '16px', color: '#fa8c16' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>{preset.name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{preset.description}</div>
                    </div>
                    <Plus style={{ width: '12px', height: '12px', color: '#999' }} />
                  </div>
                </Card>
              ))}
            </Space>
          </div>
        </div>

        {/* LLM Presets */}
        <div>
          <Title level={5}>LLM Presets</Title>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Pre-configured LLM nodes for different use cases
          </Text>
          <div style={{ marginTop: '8px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {llmPresets.map((preset) => (
                <Card
                  key={preset.name}
                  size="small"
                  style={{
                    cursor: 'grab',
                    border: '2px dashed #b37feb',
                    backgroundColor: '#f9f0ff',
                    marginBottom: '8px',
                    transition: 'all 0.3s'
                  }}
                  hoverable
                  draggable
                  onDragStart={(event) => {
                    const nodeData = {
                      type: 'llm',
                      data: {
                        label: preset.name,
                        model: preset.model,
                        description: preset.description,
                        customCode: preset.customCode || null,
                        hasCustomCode: !!preset.customCode
                      }
                    };
                    
                    const dataString = JSON.stringify(nodeData);
                    event.dataTransfer.setData('application/reactflow', dataString);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={(event) => {
                    event.currentTarget.style.cursor = 'grab';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <BrainCircuit style={{ width: '16px', height: '16px', color: '#722ed1' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>{preset.name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{preset.description}</div>
                    </div>
                    <Plus style={{ width: '12px', height: '12px', color: '#999' }} />
                  </div>
                </Card>
              ))}
            </Space>
          </div>
        </div>

        {/* Usage Instructions */}
        <div>
          <Title level={5}>How to Use</Title>
          <Space direction="vertical" size="small">
            <Text style={{ fontSize: '12px' }}>
              • Drag and drop nodes onto the canvas
            </Text>
            <Text style={{ fontSize: '12px' }}>
              • Connect nodes by dragging from output to input handles
            </Text>
            <Text style={{ fontSize: '12px' }}>
              • Click nodes to configure their properties
            </Text>
            <Text style={{ fontSize: '12px' }}>
              • Use the Execute button to run your workflow
            </Text>
          </Space>
        </div>
      </Space>
    </Drawer>
  );
};

export default NodePalette;